import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import { usePageMeta, injectJsonLd } from '@/lib/seo';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Settings, RefreshCw, Zap, Crown, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useSubscription as useSubscriptionLegacy } from '@/lib/subscription-utils';
import { StripeService } from '@/lib/stripe';
import { STRIPE_PRODUCTS } from '@/stripe-config';
import { useSubscription } from '@/hooks/use-subscription';
import { assets } from '@/lib/assets';
import { AuthConfigBanner } from '@/components/auth-config-banner';

// Types
interface WorkflowUsageStats {
  totalWorkflows: number;
  convertedWorkflows: number;
  lastConverted: string | null;
  monthlyUsage: number;
  monthlyLimit: number;
}





// Real subscription hook using Stripe and Supabase
function useRealSubscription() {
  const [isPurchasing, setIsPurchasing] = useState(false);
  const legacySubscription = useSubscriptionLegacy();
  const {
    subscriptionStatus,
    purchasePlan,
    isPlanActive,
    isPlanAvailable,
    loading: subscriptionLoading
  } = useSubscription();

  const handlePurchase = async (planCode: string) => {
    if (import.meta.env.DEV) {
      console.log('🔍 PRICING DEBUG: handlePurchase called');
      console.log('🔍 PRICING DEBUG: Plan code:', planCode);
    }

    setIsPurchasing(true);
    try {
      if (!isPlanAvailable(planCode as any)) {
        console.error('❌ PRICING DEBUG: Plan not available for code:', planCode);
        throw new Error('Plan not available');
      }

      const success = await purchasePlan(planCode as any);
      if (success) {
        if (import.meta.env.DEV) console.log('✅ PRICING DEBUG: Checkout session created, redirecting...');
        return true;
      }
      console.error('❌ PRICING DEBUG: purchasePlan returned false');
      throw new Error('Failed to create checkout session');
    } catch (error) {
      console.error('❌ PRICING DEBUG: Purchase failed:', error);
      toast.error('Failed to start checkout process. Please try again.');
      return false;
    } finally {
      if (import.meta.env.DEV) console.log('🔍 PRICING DEBUG: Purchase flow completed, setting isPurchasing to false');
      setIsPurchasing(false);
    }
  };

  const cancelSubscription = async () => {
    toast.info('Redirecting to subscription management...');
    const portalSession = await StripeService.createPortalSession();
    if (portalSession?.url) {
      window.location.href = portalSession.url;
    }
  };

  const refreshSubscription = async () => {
    return true;
  };

  return {
    isPurchasing,
    subscription: legacySubscription.subscription ? {
      status: legacySubscription.subscription.subscription_status || 'inactive',
      plan: subscriptionStatus.plan,
      planCode: subscriptionStatus.planCode,
      isActive: subscriptionStatus.isActive,
      currentPeriodEnd: legacySubscription.subscription.current_period_end
    } : {
      status: 'inactive',
      plan: 'free',
      planCode: null,
      isActive: false,
      currentPeriodEnd: null
    },
    handlePurchase,
    cancelSubscription,
    refreshSubscription,
    isLoading: subscriptionLoading,
    isPlanActive,
    error: legacySubscription.error
  };
}

export function PricingPage() {
  usePageMeta({
    title: 'Pricing – n8n2py',
    description: 'Simple, predictable pricing. Convert 1 workflow for free, then upgrade for higher limits and features.',
    image: assets.socialImage,
    url: typeof window !== 'undefined' ? window.location.href : undefined,
  });

  React.useEffect(() => {
    const cleanup = injectJsonLd({
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: 'n8n2py',
      description: 'Convert n8n JSON ↔ Python; generate FastAPI projects and n8n workflows',
      offers: {
        '@type': 'AggregateOffer',
        lowPrice: '0',
        priceCurrency: 'USD',
      },
    });
    return cleanup;
  }, []);
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();


  // SECURITY AUDIT: Check for CSRF protection
  useEffect(() => {
    // Check if CSRF token is present in meta tags or headers
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    if (!csrfToken) {
      console.warn('SECURITY WARNING: No CSRF token found in page. Potential CSRF vulnerability.');
    }
    
    // Ensure we have a CSRF cookie for Stripe/API calls
    import('@/lib/security/csrf').then(({ ensureCSRFToken }) => {
       ensureCSRFToken();
    });
  }, []);
  // const [showWaitlist, setShowWaitlist] = useState(false); // unused
  const [loading, setLoading] = useState(true);
  const [usageStats, setUsageStats] = useState<WorkflowUsageStats>({
    totalWorkflows: 0,
    convertedWorkflows: 0,
    lastConverted: null,
    monthlyUsage: 0,
    monthlyLimit: 1 // Free tier limit
  });
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const { isPurchasing, subscription, handlePurchase, isLoading: subscriptionLoading } = useRealSubscription();
  // const [showContactModal, setShowContactModal] = useState(false);

  const [paywallOpen, setPaywallOpen] = useState(false);
  const [stripeConfigured, setStripeConfigured] = useState(false);

  // Mock health check
  const runHealthCheck = async () => {
    try {
      // Simulate health check
      await new Promise(resolve => setTimeout(resolve, 500));
      console.log('Health check passed');
    } catch (error) {
      console.error('Health check failed:', error);
    }
  };

  useEffect(() => {
    runHealthCheck();
    if (user) {
      loadUsageStats();
    } else {
      // No user: don't show per-account usage; stop loading state
      setLoading(false);
    }
    // Detect Stripe client config for UI state
    try {
      const configured = StripeService.isConfigured();
      setStripeConfigured(configured);
    } catch {
      setStripeConfigured(false);
    }
  }, [user]);

  const loadUsageStats = async () => {
    try {
      if (!user) {
        setLoading(false);
        return;
      }

      // Use direct Supabase query to get workflow count
      const { getSupabaseClient } = await import('@/lib/supabase');
      const supabase = getSupabaseClient();

      const { data: workflows, error } = await (supabase
        .from('workflows') as any)
        .select('id, created_at')
        .eq('user_id', user.id);

      if (error) {
        console.error('Error loading workflow stats:', error);
        setUsageStats({
          totalWorkflows: 0,
          convertedWorkflows: 0,
          lastConverted: null,
          monthlyUsage: 0,
          monthlyLimit: 1 // Force limit to 1
        });
      } else {
        const workflowCount = workflows?.length || 0;
        const lastConverted = workflows && workflows.length > 0
          ? workflows.sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0].created_at
          : null;

        setUsageStats({
          totalWorkflows: workflowCount,
          convertedWorkflows: workflowCount,
          lastConverted,
          monthlyUsage: workflowCount,
          monthlyLimit: 1 // Force limit to 1
        });
      }
    } catch (error) {
      console.error('Failed to load usage stats:', error);
      setUsageStats({
        totalWorkflows: 0,
        convertedWorkflows: 0,
        lastConverted: null,
        monthlyUsage: 0,
        monthlyLimit: 1 // Force limit to 1
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradeClick = async (planCode: string, e?: React.MouseEvent<HTMLButtonElement>) => {
    console.log('🔍 PRICING DEBUG: Upgrade click initiated');
    console.log('🔍 PRICING DEBUG: Plan code:', planCode);
    console.log('🔍 PRICING DEBUG: User:', user ? 'authenticated' : 'not authenticated');

    if (e) e.preventDefault();

    if (planCode === 'enterprise') {
      console.log('🔍 PRICING DEBUG: Enterprise plan selected, showing contact modal');
      // setShowContactModal(true);
      return;
    }

    if (!user) {
      toast.error('Please sign in to upgrade your plan');
      navigate('/login?redirect=/pricing');
      return;
    }

    setSelectedPlan(planCode);

    try {
      // Add timeout to prevent hanging requests
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Request timed out. Please try again.')), 30000);
      });

      const purchasePromise = handlePurchase(planCode);
      const success = await Promise.race([purchasePromise, timeoutPromise]) as boolean;

      if (success) {
        toast.success('Redirecting to checkout...');
        // The handlePurchase function will redirect to Stripe
      } else {
        toast.error('Failed to start checkout process. Please try again.');
      }
    } catch (error) {
      console.error('❌ PRICING DEBUG: Upgrade error:', error);

      // Provide more specific error messages
      let errorMessage = 'An error occurred. Please try again.';

      if (error instanceof Error) {
        if (error.message.includes('authentication') || error.message.includes('sign in')) {
          errorMessage = 'Please sign in again and try upgrading.';
          navigate('/login?redirect=/pricing');
        } else if (error.message.includes('Network error') || error.message.includes('fetch')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else if (error.message.includes('timeout')) {
          errorMessage = 'Request timed out. Please try again.';
        } else if (error.message.includes('not authenticated')) {
          errorMessage = 'Please sign in to continue with your purchase.';
          navigate('/login?redirect=/pricing');
        } else {
          errorMessage = error.message;
        }
      }

      toast.error(errorMessage);
    }
  };

  // removed unused handlers: handleManageSubscription, handleDashboardClick



  const planCards = [
    {
      id: 'free',
      name: 'Free',
      price: 0,
      period: 'forever',
      description: 'Perfect for getting started',
      icon: <Zap className="h-6 w-6 text-yellow-500" />,
      features: [
        '1 free conversion per account',
        'Download Python + FastAPI code, or generate n8n JSON',
        'Self-host via Docker or Uvicorn',
        'Community support',
        'Basic export formats'
      ],
      buttonText: user ? (subscription.plan === 'free' ? 'Current Plan' : 'Get Started') : 'Get Started Free',
      buttonVariant: 'outline' as const,
      popular: false,
      disabled: false,
      showProgress: true,
      current: user && (subscription.plan === 'free' || !subscription.isActive),
      action: () => {
        if (!user) {
          navigate('/login?redirect=/pricing');
        } else if (subscription.plan === 'free') {
          return;
        } else {
          toast.info('Please contact support to downgrade your plan');
        }
      }
    },
    ...STRIPE_PRODUCTS.map(product => {
      // Override text for Pro and Lifetime to ensure accuracy
      let description = product.description;
      let features = product.features;

      if (product.planCode === 'pro-yearly' || product.planCode === 'pro-monthly') {
        description = 'For power users needing unlimited conversions';
        features = [
          'Unlimited workflow conversions',
          'VS Code Extension access',
          'Priority email support',
          'Docker deployment configurations',
          'Advanced export options'
        ];
      } else if (product.planCode === 'lifetime') {
        description = 'Pay once, own it forever';
        features = [
          'Everything in Pro',
          'One-time payment',
          'Lifetime updates included',
          'No monthly fees'
        ];
      }

      return {
      id: product.planCode,
      name: product.name,
      price: product.price,
      period: product.interval === 'month' ? '/month' : product.interval === 'year' ? '/year' : 'one-time',
      description,
      icon: product.planCode === 'lifetime'
        ? <Crown className="h-6 w-6 text-amber-500" />
        : <Crown className="h-6 w-6 text-purple-500" />,
      features,
      highlight: product.highlight,
      buttonText: !stripeConfigured
        ? 'Unavailable'
        : subscription.planCode === product.planCode && subscription.isActive
          ? 'Current Plan'
          : (product.planCode === 'lifetime' ? 'Buy Lifetime' : `Upgrade to ${product.name}`),
      buttonVariant: product.planCode === 'lifetime' ? 'secondary' as const : 'default' as const,
      popular: product.planCode === 'pro-yearly',
      disabled: (!stripeConfigured) || (isPurchasing && selectedPlan === product.planCode),
      showProgress: false,
      current: user && subscription.planCode === product.planCode && subscription.isActive,
      action: () => handleUpgradeClick(product.planCode)
    }}),
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 'Custom',
      period: '',
      description: 'For organizations',
      icon: <Settings className="h-6 w-6 text-blue-500" />,
      features: [
        'Everything in Pro, plus:',
        'Custom migration assistance',
        'Cloud Services integration',
        'HIPAA & SOC2 compliance',
        'Custom dashboard integration',
        'Dedicated support & SLAs',
        'Custom deployment options'
      ],
      buttonText: 'Contact Us',
      buttonVariant: 'outline' as const,
      popular: false,
      disabled: false,
      showProgress: false,
      current: false,
      action: () => {
        const subject = encodeURIComponent('Enterprise Plan Inquiry');
        const body = encodeURIComponent(
          `Hi n8n2py team,

We’re interested in the Enterprise plan.
Company: ____
Team size: ____
Use case: ____
Timeline: ____

Best,
`
        );
        window.location.href = `mailto:contact@n8n2py.me?subject=${subject}&body=${body}`;
      }
    },
  ];

  // Calculate usage percentage for the progress bar (3 free workflows)
  const usagePercentage = usageStats
    ? Math.min(100, Math.round((usageStats.monthlyUsage / usageStats.monthlyLimit) * 100))
    : 0;

  // Removed old ContactModal in favor of direct mailto link for Enterprise

  // Show loading skeleton if data is loading
  if (loading || authLoading || subscriptionLoading) {
    return (
      <div className="container mx-auto px-4 py-12 max-w-7xl">
        <div className="text-center mb-16">
          <Skeleton className="h-12 w-1/3 mx-auto mb-4" />
          <Skeleton className="h-6 w-1/2 mx-auto" />
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="relative overflow-hidden">
              <CardHeader>
                <Skeleton className="h-6 w-1/2 mb-2" />
                <Skeleton className="h-4 w-3/4" />
              </CardHeader>
              <CardContent className="space-y-4">
                <Skeleton className="h-8 w-1/3" />
                <div className="space-y-2">
                  {[1, 2, 3, 4, 5].map((j) => (
                    <div key={j} className="flex items-center">
                      <Skeleton className="h-4 w-4 rounded-full mr-2" />
                      <Skeleton className="h-4 w-3/4" />
                    </div>
                  ))}
                </div>
                <Skeleton className="h-10 w-full mt-4" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-12 max-w-7xl">
      <AuthConfigBanner page="pricing" />

      <div className="space-y-10">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl mb-4">
            Simple, transparent pricing
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Start with our free tier and upgrade when you're ready. No hidden fees.
          </p>

        </div>

        {/* Usage Stats for Free Plan (only when authenticated) */}
        {user && subscription && subscription.plan === 'free' && (
          <Card className="mb-10">
            <CardHeader>
              <CardTitle className="flex items-center">
                <RefreshCw className="h-5 w-5 mr-2 text-muted-foreground" />
                Your Monthly Usage
              </CardTitle>
              <CardDescription>
                {Math.min(usageStats.monthlyUsage, usageStats.monthlyLimit)} of {usageStats.monthlyLimit} free workflow conversions used
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between text-sm" data-component-name="PricingPage">
                  <span>0</span>
                  <span>{usageStats.monthlyLimit} limit</span>
                  <span>{usageStats.monthlyLimit}</span>
                </div>
                <Progress value={usagePercentage} className="h-2" />
                <p className="text-sm text-muted-foreground text-right">
                  Limited to 1 conversion per account on Free
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pricing Plans */}
        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {planCards.map((plan) => {
            const isCurrentPlan = plan.current;
            const isPaidPlan = typeof plan.price === 'number' && plan.id !== 'free';
            const isLoading = selectedPlan === plan.id && isPurchasing;

            return (
              <Card
                key={plan.id}
                className={cn(
                  'relative flex flex-col h-full transition-all duration-200 hover:shadow-xl hover:-translate-y-1 cursor-pointer',
                  plan.popular ? 'border-2 border-primary shadow-lg' : 'border border-border hover:border-primary/50',
                  isCurrentPlan && 'ring-2 ring-primary'
                )}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-black text-white text-xs font-medium px-4 py-1.5 rounded-full shadow-md">
                    Most Popular
                  </div>
                )}

                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-2xl flex items-center">
                        {plan.icon}
                        <span className="ml-2">{plan.name}</span>
                      </CardTitle>
                      <CardDescription className="mt-1">
                        {plan.description}
                      </CardDescription>
                    </div>
                    {isCurrentPlan && (
                      <Badge variant="secondary" className="text-xs">
                        Current Plan
                      </Badge>
                    )}
                  </div>
                </CardHeader>

                <CardContent className="flex-1 space-y-4">
                  <div className="mt-4">
                    <div className="text-4xl font-bold">
                      {typeof plan.price === 'number' ? `$${plan.price}` : plan.price}
                      {typeof plan.price === 'number' && plan.period !== 'one-time' && (
                        <span className="text-lg font-normal text-muted-foreground">
                          {plan.period}
                        </span>
                      )}
                      {plan.period === 'one-time' && (
                        <span className="text-sm font-normal text-muted-foreground block">
                          one-time payment
                        </span>
                      )}
                    </div>
                    {plan.id === 'pro-yearly' && (
                      <p className="text-sm text-muted-foreground mt-1">
                        Only $4.08/month when billed annually
                      </p>
                    )}
                  </div>

                  <ul className="space-y-3">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start">
                        <Check className={cn(
                          "h-5 w-5 flex-shrink-0 text-green-500 mr-2 mt-0.5",
                          isPaidPlan && plan.popular ? 'text-primary' : 'text-green-500'
                        )} />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>

                <CardFooter className="mt-auto pt-4">
                  <Button
                    className={cn(
                      "w-full bg-foreground text-background hover:bg-foreground/90",
                      isCurrentPlan && 'bg-muted text-muted-foreground hover:bg-muted',
                    )}
                    size="lg"
                    disabled={plan.disabled || !!isCurrentPlan}
                    onClick={(e) => {
                      e.preventDefault();
                      plan.action();
                    }}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      plan.buttonText
                    )}
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>

        {/* FAQ Section */}
        <div className="max-w-3xl mx-auto mb-16">
          <h2 className="text-2xl font-bold text-center mb-8">Frequently Asked Questions</h2>

          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">What payment methods do you accept?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  We accept all major credit cards including Visa, Mastercard, American Express, and Discover.
                  We also support payments via PayPal and bank transfers for annual subscriptions.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Can I cancel my subscription at any time?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Yes, you can cancel your subscription at any time. Your subscription will remain active until the end of your current billing period, and you won't be charged again.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Is there a free trial available?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Yes! Our free tier allows you to convert 1 workflow per account at no cost.
                  You can upgrade to a paid plan at any time to unlock more features and higher limits.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">What's included in the Lifetime plan?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  The Lifetime plan includes all features of the Pro plan with a one-time payment of $400. You'll get unlimited conversions, all updates, and full support forever.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Feature Comparison */}
        <div id="pricing-comparison" className="mt-16">
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">Pricing Comparison</CardTitle>
              <CardDescription>
                Compare the features included in each plan
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr>
                      <th className="text-left py-4 px-4">Features</th>
                      <th className="text-center py-4 px-4">Free</th>
                      <th className="text-center py-4 px-4">Pro</th>
                      <th className="text-center py-4 px-4">Enterprise</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    <tr>
                      <td className="py-4 px-4 font-medium">Workflow Conversions</td>
                      <td className="text-center py-4 px-4">1 total</td>
                      <td className="text-center py-4 px-4">Unlimited</td>
                      <td className="text-center py-4 px-4">Unlimited</td>
                    </tr>

                    <tr>
                      <td className="py-4 px-4 font-medium">VS Code Extension</td>
                      <td className="text-center py-4 px-4 text-sm text-muted-foreground">1 conversion</td>
                      <td className="text-center py-4 px-4"><Check className="h-5 w-5 mx-auto text-green-500" /></td>
                      <td className="text-center py-4 px-4"><Check className="h-5 w-5 mx-auto text-green-500" /></td>
                    </tr>

                    <tr>
                      <td className="py-4 px-4 font-medium">Support</td>
                      <td className="text-center py-4 px-4">Community</td>
                      <td className="text-center py-4 px-4">Priority Email (24–48h)</td>
                      <td className="text-center py-4 px-4">24/7 SLA</td>
                    </tr>
                    <tr>
                      <td className="py-4 px-4 font-medium">Docker Support</td>
                      <td className="text-center py-4 px-4"><Check className="h-5 w-5 mx-auto text-green-500" /></td>
                      <td className="text-center py-4 px-4"><Check className="h-5 w-5 mx-auto text-green-500" /></td>
                      <td className="text-center py-4 px-4"><Check className="h-5 w-5 mx-auto text-green-500" /></td>
                    </tr>






                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Paywall Modal */}
      {paywallOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setPaywallOpen(false)} />
          <div className="bg-background rounded-lg shadow-xl z-10 w-full max-w-md p-6 border">
            <h2 className="text-2xl font-bold mb-4">Upgrade to Pro</h2>
            <p className="text-muted-foreground mb-6">
              You've reached the limit of the free plan. Upgrade to Pro to continue converting workflows without restrictions.
            </p>

            <div className="space-y-4">
              <Button
                className="w-full"
                size="lg"
                onClick={() => handleUpgradeClick('pro')}
                disabled={isPurchasing}
              >
                {isPurchasing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  `Upgrade to Pro - $49/year`
                )}
              </Button>

              <Button
                variant="outline"
                className="w-full"
                onClick={() => setPaywallOpen(false)}
                disabled={isPurchasing}
              >
                Maybe Later
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
};

export default PricingPage;
